import {
  HandoffContinuePage,
  RequireMembership,
  RequireModule,
  RequirePermission,
  RequireSession,
  RequireTenant,
  RequireTenantMfa,
  RouteErrorPage,
  SecurityPage,
  SignInPage,
  TenantRedirect,
  useStartupChecks,
  type PermissionQuery,
} from "@how/app-kit";
import { Navigate, createBrowserRouter, useLocation } from "react-router";
import { EmptyState } from "@how/ui";
import { HomePage } from "../features/home/HomePage";
import { ProfilePage } from "../features/profile/ProfilePage";
import { EventsPage } from "../features/events/EventsPage";
import { GroupsPage } from "../features/groups/GroupsPage";
import { MySchedulePage } from "../features/my-schedule/MySchedulePage";
import { CarePage } from "../features/care/CarePage";
import { ShopPage } from "../features/shop/ShopPage";
import { SermonsPage } from "../features/sermons/SermonsPage";
import { DevotionalsPage } from "../features/devotionals/DevotionalsPage";

import { env } from "../lib/env";
import { Shell } from "./Shell";

/** The portal's own gate, started with the other start-up checks rather than after them (#286). */
const PORTAL_CHECKS: readonly PermissionQuery[] = [{ permission: "portal.access" }];

/**
 * Serving became My schedule (ADR 0070 R1): /serving and anything under it go to /my-schedule itself, which has no
 * pages under it, keeping only the query and hash.
 */
export function ServingMoved() {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: "/my-schedule", search, hash }} replace />;
}

function TenantTree() {
  useStartupChecks(PORTAL_CHECKS);
  return (
    <RequireTenant>
      <RequireSession>
        <RequireMembership>
          <RequireTenantMfa>
            <RequirePermission permission="portal.access">
              <Shell />
            </RequirePermission>
          </RequireTenantMfa>
        </RequireMembership>
      </RequireSession>
    </RequireTenant>
  );
}

export function createAppRouter() {
  return createBrowserRouter([
    {
      errorElement: <RouteErrorPage />,
      path: "/sign-in",
      element: (
        <RequireTenant>
          <SignInPage
            title="HoW Member Portal"
            subtitle="Member sign-in"
            footnote="Access is by invitation from your church."
          />
        </RequireTenant>
      ),
    },
    {
      errorElement: <RouteErrorPage />,
      // The hand-off lands here (spec section 6): no session yet, no church shell.
      path: "/auth/continue",
      element: <HandoffContinuePage env={env} />,
    },
    {
      errorElement: <RouteErrorPage />,
      path: "/t/:slug",
      element: <TenantRedirect env={env} surface="member_portal" />,
    },
    {
      errorElement: <RouteErrorPage />,
      path: "/",
      element: <TenantTree />,
      children: [
        { index: true, element: <HomePage /> },
        // Aurora, the same Home in the night-and-glass design (preview, restored 2026-10-13). The old /home2/1 to
        // /home2/4 addresses open it too.
        { path: "home2", element: <HomePage design="aurora" /> },
        { path: "home2/*", element: <Navigate to="/home2" replace /> },
        // The invitation email's link (invite-member). RequireMembership above has already offered the invitation
        // and accepted it; here there is nothing left to do but go home.
        { path: "accept-invitation", element: <Navigate to="/" replace /> },
        { path: "profile", element: <ProfilePage /> },
        {
          path: "groups",
          element: (
            <RequireModule module="groups">
              <GroupsPage />
            </RequireModule>
          ),
        },
        {
          path: "shop",
          element: (
            <RequireModule module="commerce">
              <ShopPage />
            </RequireModule>
          ),
        },
        {
          path: "events",
          element: (
            <RequireModule module="events">
              <EventsPage />
            </RequireModule>
          ),
        },
        {
          path: "sermons",
          element: (
            <RequireModule module="sermons_media">
              <SermonsPage />
            </RequireModule>
          ),
        },
        {
          path: "devotionals",
          element: (
            <RequireModule module="devotionals">
              <DevotionalsPage />
            </RequireModule>
          ),
        },
        {
          path: "my-schedule",
          element: (
            <RequireModule module="services">
              <MySchedulePage />
            </RequireModule>
          ),
        },
        { path: "serving", element: <ServingMoved /> },
        { path: "serving/*", element: <ServingMoved /> },
        { path: "care", element: <CarePage /> },
        { path: "security", element: <SecurityPage /> },
        {
          path: "*",
          element: (
            <EmptyState
              title="Page not found"
              description="Check the address or use the navigation."
            />
          ),
        },
      ],
    },
  ]);
}
